import { useEffect, useState } from 'react';

// ¿La app está instalada en este dispositivo? y, si no, cómo se instala.
// Mismo patrón ya probado en otro proyecto (CorrerPro):
// - Abierta desde el ícono: se sabe siempre (display-mode: standalone, o
//   navigator.standalone en iPhone).
// - Android/Chrome avisan que se puede instalar con `beforeinstallprompt`:
//   se guarda ese evento para ofrecer un botón "Instalar" de un toque.
// - iPhone no tiene nada parecido: ahí va la guía manual (Compartir →
//   Agregar a inicio).
// No se usa Service Worker en esta app (ver comentario en index.js — el
// uso siempre necesita el backend vivo), pero no hace falta: el manifest
// con display:"standalone" ya alcanza para que el navegador la considere
// instalable.

let eventoInstalar = null;
let recienInstalada = false;
let escuchando = false;
const oyentes = new Set();
const avisar = () => oyentes.forEach(f => f());

// Se llama una vez al cargar la app: el evento llega una sola vez por carga de página.
export function escucharInstalacion() {
  if (typeof window === 'undefined' || escuchando) return;
  escuchando = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // sin el cartelito automático del navegador: lo ofrecemos nosotros
    eventoInstalar = e;
    avisar();
  });
  window.addEventListener('appinstalled', () => {
    eventoInstalar = null;
    recienInstalada = true;
    avisar();
  });
}

export function abiertaComoApp() {
  if (typeof window === 'undefined') return false;
  return ['standalone', 'fullscreen', 'minimal-ui'].some(m => window.matchMedia?.(`(display-mode: ${m})`).matches)
    || window.navigator.standalone === true
    || document.referrer.startsWith('android-app://');
}

export function detectarDispositivo() {
  if (typeof navigator === 'undefined') return { so: 'otro', navegador: 'otro', celular: false };
  const ua = navigator.userAgent;
  // El iPad con iPadOS 13+ se presenta como Mac: se lo reconoce por la pantalla táctil
  const ios = /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  const android = /android/i.test(ua);
  let navegador = 'otro';
  if (/instagram|fban|fbav|fb_iab|line\/|tiktok|musical_ly|; wv\)/i.test(ua)) navegador = 'app';
  else if (/samsungbrowser/i.test(ua)) navegador = 'samsung';
  else if (/edg(a|ios|e)?\//i.test(ua)) navegador = 'edge';
  else if (/fxios|firefox/i.test(ua)) navegador = 'firefox';
  else if (/crios|chrome/i.test(ua)) navegador = 'chrome';
  else if (/safari/i.test(ua)) navegador = 'safari';
  return { so: ios ? 'ios' : android ? 'android' : 'otro', navegador, celular: ios || android };
}

export function useInstalacion() {
  const [, refrescar] = useState(0);
  const [dispositivo, setDispositivo] = useState(null);
  const [comoApp, setComoApp] = useState(false);

  useEffect(() => {
    setDispositivo(detectarDispositivo());
    setComoApp(abiertaComoApp());
    const oyente = () => refrescar(n => n + 1);
    oyentes.add(oyente);
    return () => { oyentes.delete(oyente); };
  }, []);

  const instalar = async () => {
    if (!eventoInstalar) return false;
    const evento = eventoInstalar;
    await evento.prompt();
    const { outcome } = await evento.userChoice;
    eventoInstalar = null; // se usa una sola vez
    avisar();
    return outcome === 'accepted';
  };

  return {
    dispositivo,
    abiertaComoApp: comoApp,
    recienInstalada,
    puedeInstalarDirecto: !!eventoInstalar,
    instalar,
  };
}
